import { HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { ContextStateService } from '@core/services/context-state.service';
import { ModuleRouteContextService } from '@core/services/modules/module-route-context.service';
import { ModuleType } from '@core/services/modules/models';
import { environment } from '@environment';
import { throwError } from 'rxjs';
import {
    IGNORE_MODULE_CONTEXT,
    IGNORE_PROJECT_CONTEXT,
    CurrentOrganizationContextHeaders,
    MODULE_TYPE_CONTEXT,
    PROJECT_ID_CONTEXT,
    REQUIRE_PROJECT_CONTEXT,
} from '@core/services/base/request-context';

@Injectable()
export class CurrentOrganizationContextInterceptor implements HttpInterceptor {
    private readonly contextStateService = inject(ContextStateService);
    private readonly location = inject(Location);
    private readonly moduleRouteContextService = inject(ModuleRouteContextService);

    public intercept(req: HttpRequest<any>, next: HttpHandler) {
        if (!req.url.startsWith(environment.apiConfig.api.uri)) {
            return next.handle(req);
        }

        if (req.context.get(IGNORE_MODULE_CONTEXT)) {
            return next.handle(req);
        }

        // Explicit per-request module wins. Selected module wins over URL for normal app routes so
        // module switches can refresh data before/while the URL catches up. Admin system-module
        // settings (`/admin/modules/:module`) are the exception - those edit platform defaults for a
        // module type unrelated to the signed-in user's selected organization module.
        const selectedModuleType =
            req.context.get(MODULE_TYPE_CONTEXT)
            ?? this.getAdminSystemModuleTypeFromUrl()
            ?? this.contextStateService.selectedModuleType()
            ?? this.getModuleTypeFromUrl();
        if (selectedModuleType === null) {
            return next.handle(req);
        }

        const projectId = req.context.get(IGNORE_PROJECT_CONTEXT)
            ? null
            : (req.context.get(PROJECT_ID_CONTEXT) ?? this.contextStateService.selectedProject()?.id ?? null);
        if (req.context.get(REQUIRE_PROJECT_CONTEXT) && projectId === null) {
            return throwError(() => new Error('Project context is required for this request.'));
        }

        const headers: Record<string, string> = {
            [CurrentOrganizationContextHeaders.moduleType]: selectedModuleType.toString(),
        };

        if (projectId !== null) {
            headers[CurrentOrganizationContextHeaders.projectId] = projectId;
        }

        return next.handle(req.clone({ setHeaders: headers }));
    }

    private getAdminSystemModuleTypeFromUrl(): ModuleType | null {
        const path = this.location.path().split(/[?#]/)[0];
        const segments = path.split('/').filter(Boolean);
        if (segments[0]?.toLowerCase() !== 'admin' || segments[1]?.toLowerCase() !== 'modules') {
            return null;
        }

        return this.moduleRouteContextService.getModuleTypeFromSlug(segments[2]);
    }

    private getModuleTypeFromUrl(): ModuleType | null {
        const path = this.location.path().split(/[?#]/)[0];
        const segments = path.split('/').filter(Boolean);
        return this.moduleRouteContextService.getModuleTypeFromSlug(segments[0]);
    }
}
